import { Location as NgLocation } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { AfterContentInit, Compiler, Component, ContentChildren, forwardRef, Inject, Injectable, Input, NgModule, QueryList, ViewChildren, ViewContainerRef } from '@angular/core';
import { MatTab } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { TEMPLATE_CONTENT } from '../../common/endpoints';
import { isOnline } from '../../common/setup';
import { Alert, Customer, Location, Organization, Partner, ServiceLevel, SubscriptionInfo, Tenant, Thing, ThingDefinition, ThingTestSession, UiProfilePage, UiTab, UiTabNavigationType, User, WorkOrder, WorkSession } from '../../model/index';
import { AppService } from '../../service/app.service';
import { AuthenticationService } from '../../service/authentication.service';
import { BreadcrumbService } from '../../service/breadcrumb.service';
import { ControlBarService } from '../../service/control-bar.service';
import { FieldService } from '../../service/field.service';
import { HttpService } from '../../service/http.service';
import { UiService } from '../../service/ui.service';
import { RegisteredWidget, WidgetRegistrationService } from '../../service/widget-registration.service';
import { AbstractContextService } from '../../shared/class/abstract-context-service.class';
import { AbstractThingContextService } from '../../shared/class/abstract-thing-context-service.class';
import { MetricDetailComponent, PropertyComponent } from '../../shared/component';
import { AggregatedValueComponent } from '../../shared/component/custom-widget-components/aggregated-value.component';
import { ThresholdComponent } from '../../shared/component/custom-widget-components/threshold.component';
import { WorkSessionsComponent } from '../../shared/component/custom-widget-components/work-sessions.component';
import { StatisticComponent } from '../../shared/component/statistic/statistic.component';
import { getContextServiceProvider } from '../../shared/provider/context-service.provider';
import { getExportContextServiceProvider } from '../../shared/provider/export-context-service.provider';
import { getThingContextServiceProvider } from '../../shared/provider/thing-context-service.provider';
import { SharedModule } from '../../shared/shared.module';
import { StyleModule } from '../../style/style.module';
import { ConfigurationParameterEntryComponent } from '../../widget/configuration-parameters/configuration-parameter-entry.component';
import { SchedulerModule } from '../../widget/scheduler/scheduler.module';
import { ThingOptionModule } from '../../widget/thing-option/thing-option.module';
import { TimeseriesWidgetModule } from '../../widget/timeseries/timeseries-widget.module';
import { UserThingAuthorizationModule } from '../../widget/user-thing-authorization/user-thing-authorization.module';
import { WidgetModule } from '../../widget/widget.module';
import { DynamicListModule } from '../dynamic-list/dynamic-list.module';
import { ThingService } from '../thing/thing.service';
import { TemplateLoadStatus } from './template-load-status';

@Injectable()
export class TemplateLoaderService {

    constructor(
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => Compiler)) private compiler: Compiler,
        @Inject(forwardRef(() => UiService)) private uiService: UiService,
        @Inject(forwardRef(() => WidgetRegistrationService)) private widgetRegistrationService: WidgetRegistrationService,
        @Inject(forwardRef(() => NgLocation)) private browserLocation: NgLocation,
        @Inject(forwardRef(() => BreadcrumbService)) private breadcrumbService: BreadcrumbService,
        @Inject(forwardRef(() => ControlBarService)) private controlBarService: ControlBarService
    ) { }

    static TEMPLATE_ERROR = `
        <div class="row">
            <div class="col-md-6 col-sm-12">
                <div class="alert alert-warning">
                    <h4><i class="icon fa fa-exclamation"></i>Template not found!</h4>
                    Please check your configuration.
                </div>
            </div>
        </div> 
    `;

    static UNAVAILABLE_FEATURE_TEMPLATE = '<unavailable-feature-banner [features]="[FEATURES]"></unavailable-feature-banner>'

    static ensureReportPdf(template: string): string {
        const cover = (window as any).__servitlyReportGeneration?.cover;
        if (typeof cover !== 'string' || cover === '' || /<report-pdf[\s>\/]/i.test(template)) {
            return template;
        }
        return '<report-pdf></report-pdf>' + template;
    }

    loadFromEndpoint(endpoint: string, vcRef: ViewContainerRef, clearView: boolean, params?: HttpParams,
        templateWrapper?: { preTemplate: string, postTemplate: string, placeholders: { [key: string]: string } }, thingForContext?: Thing, locationForContext?: Location, emptyContext: boolean = false,
        ensureReportPdf: boolean = false): Promise<any> {
        return firstValueFrom(this.httpService.getText(endpoint, params))
            .then(template => this.loadFromTextWithWrapper(ensureReportPdf ? TemplateLoaderService.ensureReportPdf(template) : template, templateWrapper, vcRef, clearView, thingForContext, locationForContext, emptyContext));
    }

    /** Returns the raw content of a template served by an endpoint. */
    getTextFromEndpoint(endpoint: string): Promise<string> {
        return firstValueFrom(this.httpService.getText(endpoint));
    }

    loadFromTextWithWrapper(template: string, templateWrapper: { preTemplate: string; postTemplate: string; placeholders: { [key: string]: string; }; }, vcRef: ViewContainerRef, clearView: boolean, thingForContext?: Thing, locationForContext?: Location, emptyContext: boolean = false) {
        if (templateWrapper) {
            for (let key in templateWrapper.placeholders) {
                template = template.replace(key, templateWrapper.placeholders[key]);
            }
            template = templateWrapper.preTemplate + template + templateWrapper.postTemplate;
        }
        this.loadFromText(template, vcRef, clearView, thingForContext, locationForContext, emptyContext);
        return template;
    }

    loadFromText(template: string, vcRef: ViewContainerRef, clearView: boolean, thingForContext?: Thing, locationForContext?: Location, emptyContext: boolean = false): Promise<void> {
        if (clearView) {
            vcRef.clear()
        }
        const dynamicComponent = this.createComponent(template, thingForContext, locationForContext, emptyContext);
        const registeredComponents = this.widgetRegistrationService.getRegisteredWidgets().map(w => this.registerComponent(w, thingForContext, locationForContext, emptyContext));
        const dynamicModule = this.createModule(dynamicComponent, registeredComponents);

        return this.compiler.compileModuleAndAllComponentsAsync(dynamicModule).then(
            compiledModule => {
                const factory = compiledModule.componentFactories.find((comp) => comp.componentType === dynamicComponent);
                if (!factory) {
                    throw new Error('Unable to create the dynamic template component');
                }
                vcRef.createComponent(factory);
            }
        );
    }

    loadThingDetailsDashboard(subPath: string, thing: Thing, vcRef: ViewContainerRef): void {
        this.uiService.getUserPagedDashboards(thing.id, 0, 1).then(dashboards => {
            let template = '';
            if (dashboards.content.length > 0) {
                let dashboard = dashboards.content[0];
                this.loadTabbedPageContent(dashboard.tabs, subPath, vcRef, thing, dashboard.controlBarTemplateName);
            } else {
                this.loadFromText(template, vcRef, true);
            }
        }).catch(() => this.loadFromText(TemplateLoaderService.TEMPLATE_ERROR, vcRef, true));
    }

    loadContentFromUiPage(vcRef: ViewContainerRef, page: UiProfilePage, subPath: string, defaultControlBarTemplateName: string): void {
        if (page) {
            this.loadTabbedPageContent(page.tabs, subPath, vcRef, null, (page.controlBarTemplateName ?? defaultControlBarTemplateName));
        } else {
            this.loadFromText(TemplateLoaderService.TEMPLATE_ERROR, vcRef, true);
        }
    }

    loadTabbedPageContent(tabs: UiTab[], subPath: string, vcRef: ViewContainerRef, thing?: Thing, defaultControlBarTemplateName?: string): void {
        const thingDefinitionId = thing?.thingDefinitionId;
        if (tabs?.length) {
            tabs = this.filterTabsBySubPathType(subPath, tabs).filter(tab => this.filterVisibleTab(tab));
            if (!tabs.length) {
                this.loadFromText('', vcRef, true);
            } else if (tabs.length == 1) { // tab is hidden
                let tab = tabs[0];
                if (tab.navigation == UiTabNavigationType.LINK) {
                    this.breadcrumbService.addSubPath(tab.title);
                }
                const templateRequest = this.getTemplateRequest(tab, thing, thingDefinitionId);
                const controlBarTemplateRequest = (tab.controlBarTemplateName || defaultControlBarTemplateName) ? this.uiService.getContentByTemplateName(tab.controlBarTemplateName || defaultControlBarTemplateName, thingDefinitionId) : Promise.resolve(null);
                Promise.all([templateRequest, controlBarTemplateRequest]).then(results => {
                    this.loadFromText(results[0], vcRef, true);
                    this.updateControlBarContent(results[1]);
                });
            } else { // show mat-tab-group
                Promise.all(tabs.map(t => this.getTemplateRequest(t, thing, thingDefinitionId))).then(templateContents => {
                    Promise.all(tabs.map(t => (t.controlBarTemplateName || defaultControlBarTemplateName) ? this.uiService.getContentByTemplateName(t.controlBarTemplateName || defaultControlBarTemplateName, thingDefinitionId) : Promise.resolve(null))).then(controlBarContents => {
                        this.controlBarService.setControlsBarTabContents(controlBarContents);
                        let template = this.buildTabbedTemplate(tabs, subPath, templateContents, thing);
                        this.loadFromText(template, vcRef, true);
                        let selectedIndex = tabs.findIndex(t => t.urlPath == subPath);
                        let controlBarContent = this.controlBarService.getControlsBarTabContent(selectedIndex > -1 ? selectedIndex : 0);
                        this.updateControlBarContent(controlBarContent);
                    });
                });
            }
        } else {
            this.loadFromText('', vcRef, true);
        }
    }

    private filterTabsBySubPathType(subPath: string, tabs: UiTab[]): UiTab[] {
        if (subPath) {
            let subTab = tabs.find(t => t.urlPath == subPath);
            if (subTab) {
                if (subTab.navigation == UiTabNavigationType.LINK) {
                    return [subTab];
                } else { // UiTabNavigationType.TAB_MENU
                    return tabs.filter(t => t.navigation == UiTabNavigationType.TAB_MENU);
                }
            } else {
                return [];
            }
        } else {
            return tabs.filter(t => t.navigation == UiTabNavigationType.TAB_MENU);
        }
    }

    private getTemplateRequest(tab: UiTab, thing: Thing, thingDefinitionId: string): Promise<string> {
        return this.isAvailable(tab, thing) ? this.uiService.getContentByTemplateName(tab.templateName, thingDefinitionId) : Promise.resolve(TemplateLoaderService.UNAVAILABLE_FEATURE_TEMPLATE.replace('FEATURES', tab.availabilityFeatures?.map(f => `'${f}'`).join(',')));
    }

    private isAvailable(tab: UiTab, thing?: Thing): boolean {
        if (!thing) {
            return true;
        }
        if (!tab.availabilityFeatures?.length) {
            return true;
        }
        const thingFeatures = thing.subscriptionInfo?.features || [];
        return thingFeatures.some(t => tab.availabilityFeatures.includes(t));
    }

    private filterVisibleTab(tab: UiTab): boolean {
        if (tab.visibilityCondition) {
            try {
                let visibilityCondition = this.uiService.normalizeVisibilityCondition(tab.visibilityCondition)
                return eval(visibilityCondition);
            } catch {
                return false;
            }
        }
        return true;
    }

    private buildTabbedTemplate(tabs: UiTab[], subPath: string, templateContents: string[], thing: Thing) {
        let selectedIndex = tabs.findIndex(t => t.urlPath == subPath);
        if (selectedIndex == -1) {
            if (subPath) {
                let path = this.browserLocation.path();
                this.browserLocation.replaceState(path.substring(0, path.lastIndexOf('/')));
            }
            subPath = '';
        }
        let template = `<tabbed-page selectedTab="${subPath}">`;
        let tabItems = tabs.map((tab, i) => {
            return `<tab-item title="${tab.title}" urlPath="${tab.urlPath}" [unavailableContent]="${!this.isAvailable(tab, thing)}"></tab-item>
                    <ng-template tab-item-content>
                        ${templateContents[i]}
                    </ng-template>`;
        });
        template += tabItems.join('\n');
        template += `</tabbed-page>`;
        return template;
    }

    loadUserTemplate(vcRef: ViewContainerRef, name: string, thingDefinitionId?: string, emptyContext: boolean = false, ensureReportPdf: boolean = false): Promise<void> {
        return this.uiService.getUserPagedTemplates(name, 0, 1, thingDefinitionId).then(templates => {
            if (templates.content.length > 0) {
                let template = templates.content[0];
                return this.loadFromEndpoint(TEMPLATE_CONTENT.replace('{id}', template.id), vcRef, true, null, null, null, null, emptyContext, ensureReportPdf);
            } else {
                return this.loadFromText('', vcRef, true);
            }
        }).catch(() => this.loadFromText(TemplateLoaderService.TEMPLATE_ERROR, vcRef, true));
    }

    loadUserTemplateWithStatus(vcRef: ViewContainerRef, name: string, thingDefinitionId?: string, emptyContext: boolean = false): Promise<TemplateLoadStatus> {
        // 1. No template configured: nothing to render (EMPTY).
        if (!name) {
            vcRef.clear();
            return Promise.resolve(TemplateLoadStatus.EMPTY);
        }
        // 2. Find the template by name.
        return this.uiService.getUserPagedTemplates(name, 0, 1, thingDefinitionId)
            .then(templates => templates.content[0])
            // 3. A configured template that does not exist is an ERROR.
            .then(template => {
                if (!template) {
                    throw new Error('Template not found');
                }
                // 4. Download its content.
                return firstValueFrom(this.httpService.getText(TEMPLATE_CONTENT.replace('{id}', template.id)));
            })
            // 5. Render it, then report LOADED.
            .then(content => this.loadFromText(content, vcRef, true, null, null, emptyContext))
            .then(() => TemplateLoadStatus.LOADED)
            // 6. Any failure above is an ERROR, never a rejection.
            .catch(() => {
                vcRef.clear();
                return TemplateLoadStatus.ERROR;
            });
    }

    private createComponent(template: string, thing?: Thing, location?: Location, emptyContext: boolean = false) {
        let thingContextServiceProvider = getThingContextServiceProvider(thing, emptyContext);
        let contextServiceProvider = getContextServiceProvider(location, emptyContext);
        let exportContextServiceProvider = getExportContextServiceProvider(emptyContext);
        @Component({
            template: template,
            viewProviders: [thingContextServiceProvider, contextServiceProvider, exportContextServiceProvider, ThingService]
        })
        class DynamicComponent {

            private currentTimestamp;

            reportMode: boolean;

            @ViewChildren(MatTab) tabs: QueryList<MatTab>

            constructor(
                @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
                @Inject(forwardRef(() => AbstractContextService)) private contextService: AbstractContextService,
                @Inject(forwardRef(() => AppService)) private appService: AppService,
                @Inject(forwardRef(() => ActivatedRoute)) private route: ActivatedRoute,
                @Inject(forwardRef(() => Router)) private router: Router,
                @Inject(forwardRef(() => AbstractThingContextService)) private thingContextService: AbstractThingContextService,
                @Inject(forwardRef(() => FieldService)) private fieldService: FieldService
            ) {
                this.route.queryParams.subscribe(params => {
                    this.reportMode = (params['mode'] == 'report') || (params['mode'] == 'report_preview') || !!(this.router.url.match(/\/dashboard\/thing_details\/[0-9A-Za-z]+\/reports/g));
                });
                this.currentTimestamp = new Date().getTime();
            }

            getThing(): Thing {
                return this.thingContextService.getCurrentThing();
            }

            getThingDefinition(): ThingDefinition {
                return this.thingContextService.getCurrentThingDefinition();
            }

            getUser(): User {
                return this.authenticationService.getUser();
            }

            getTenant(): Tenant {
                return this.authenticationService.getTenant();
            }

            getCustomer(): Customer {
                let user = this.getUser();
                return user && user.customer ? user.customer : this.contextService.getCurrentCustomer();
            }

            getLocation(): Location {
                let user = this.getUser();
                return user && user.location ? user.location : this.contextService.getCurrentLocation();
            }

            getPartner(): Partner {
                let user = this.getUser();
                let currentPartner = this.contextService.getCurrentPartner();
                return currentPartner || user.partner;
            }

            getOrganization(): Organization {
                return this.contextService.getCurrentOrganization();
            }

            getServiceLevel(): ServiceLevel {
                let thing = this.thingContextService.getCurrentThing();
                if (thing) {
                    return thing.serviceLevel;
                }
                return null;
            }

            getTestSession(): ThingTestSession {
                return this.thingContextService.getCurrentThingTestSession();
            }

            getTestSessionStartDate(): number {
                let thingTestSession = this.getTestSession();
                return thingTestSession ? thingTestSession.startedAt : null;
            }

            getTestSessionStopDate(): number {
                let thingTestSession = this.getTestSession();
                return thingTestSession ? thingTestSession.stoppedAt : null;
            }

            isMobile(): boolean {
                return this.appService.isMobile();
            }

            isTablet(): boolean {
                return this.appService.isTablet();
            }

            getUserCustomer(): Customer {
                return this.authenticationService.getUserCustomer();
            }

            getCustomerServiceLevel(): ServiceLevel {
                let customer = this.getCustomer();
                return customer ? customer.serviceLevel : null;
            }

            getWorkSession(): WorkSession {
                return this.thingContextService.getCurrentWorkSession();
            }

            getTags(): string[] {
                return this.contextService.getTags();
            }

            getCustomerTags(): string[] {
                return this.contextService.getCustomerTags();
            }

            getLocationTags(): string[] {
                return this.contextService.getLocationTags();
            }

            getPartnerTags(): string[] {
                return this.contextService.getPartnerTags();
            }

            getCurrentTimestamp(): number {
                return this.currentTimestamp;
            }

            getAlert(): Alert {
                return this.thingContextService.getCurrentAlert();
            }

            getEvent(): Alert {
                return this.thingContextService.getCurrentAlert();
            }

            hasPlan(planName: string): boolean {
                if (this.authenticationService.isOrganizationUser()) {
                    return true;
                }
                return this.getSubscription()?.plan == planName;
            }

            hasAddOn(addOnName: string): boolean {
                if (this.authenticationService.isOrganizationUser()) {
                    return true;
                }
                return this.getSubscription()?.addOns?.includes(addOnName);
            }

            hasFeature(...featureNames: string[]): boolean {
                if (this.authenticationService.isOrganizationUser()) {
                    return true;
                }
                return featureNames.some(f => this.getSubscription()?.features?.includes(f));
            }

            getSubscription(): SubscriptionInfo {
                if (this.thingContextService.getCurrentThing()) {
                    return this.thingContextService.getSubscription();
                } else if (this.authenticationService.isPartnerUser()) {
                    return this.authenticationService.getUser().partner?.subscriptionInfo;
                } else if (this.authenticationService.isCustomerUser()) {
                    return this.authenticationService.getUser().customer?.subscriptionInfo;
                }
                const customerCtx = this.contextService.getCurrentCustomer();
                if (customerCtx) {
                    return customerCtx.subscriptionInfo;
                }
                const partnerCtx = this.contextService.getCurrentPartner();
                if (partnerCtx) {
                    return partnerCtx.subscriptionInfo;
                }
                return null;
            }

            isTrialActive(): boolean {
                return this.getSubscription()?.trialActive;
            }

            getTrialStartTimestamp(): number {
                return this.getSubscription()?.trialStartTimestamp;
            }

            getTrialEndTimestamp(): number {
                return this.getSubscription()?.trialEndTimestamp;
            }

            isOnline(): boolean {
                return isOnline();
            }

            getParentThing(): Thing {
                return this.thingContextService.getParentThing();
            }

            getRootThing(): Thing {
                return this.thingContextService.getRootThing();
            }

            getWorkOrder(): WorkOrder {
                return this.thingContextService.getCurrentWorkOrder();
            }

            getValueByFieldRef(name: string, defaultValue?: any) {
                return this.fieldService.getValue(name, defaultValue);
            }
        }
        return DynamicComponent;
    }

    registerComponent(widget: RegisteredWidget, thing?: Thing, location?: Location, emptyContext: boolean = false) {
        let thingContextServiceProvider = getThingContextServiceProvider(thing, emptyContext);
        let contextServiceProvider = getContextServiceProvider(location, emptyContext);
        let exportContextServiceProvider = getExportContextServiceProvider(emptyContext);
        @Component({
            selector: widget.tagName,
            template: `
                <widget *ngIf="visible" name="${widget.className}" [title]="title" [config]="config" [inputs]="inputs" [metricSubscriptionDisabled]="metricSubscriptionDisabled" [lazyMetricDataLoading]="lazyMetricDataLoading" [collapsible]="collapsible" [description]="description">
                    <metric *ngFor="let m of metricComponents" [id]="m.id" [name]="m.name" [label]="m.label" [filter]="m.filter" [x]="m.x" [y]="m.y" [chartOptions]="m.chartOptions" [icon]="m.icon" [unit]="m.unit" [aggregation]="m.aggregation" [config]="m.config"></metric>
                    <property *ngFor="let p of propertyComponents" [id]="p.id" [name]="p.name" [label]="p.label" [filter]="p.filter" [x]="p.x" [y]="p.y" [config]="p.config"></property>
                    <configuration-parameter *ngFor="let p of configParamsComponents" [name]="p.name" [label]="p.label" [config]="p.config"></configuration-parameter>
                    <statistic *ngFor="let s of statisticComponents" [name]="s.name" [label]="s.label" [limit]="s.limit" [thingDefinition]="s.thingDefinition" [sumMetric]="s.sumMetric" [groupBy]="s.groupBy" [query]="s.query" [aggregation]="s.aggregation" [property]="s.property" [filter]="s.filter" [description]="s.description" [config]="s.config" [resource]="s.resource" [activationType]="s.activationType" [sortDirection]="s.sortDirection" [averagedBy]="s.averagedBy"></statistic>
                    <work-sessions *ngFor="let ws of workSessionsComponents" [categoryProperty]="ws.categoryProperty" [valueProperty]="ws.valueProperty" [valueFilter]="ws.valueFilter" [query]="ws.query" [sort]="ws.sort" [chartType]="ws.chartType" [unit]="ws.unit" [label]="ws.label"  [config]="ws.config"></work-sessions>
                    <threshold *ngFor="let th of thresholds" [label]="th.label" [valueProperty]="th.valueProperty" [value]="th.value" [color]="th.color" [config]="th.config" [horizontalLabelAlignment]="th.horizontalLabelAlignment" [verticalLabelAlignment]="th.verticalLabelAlignment" [direction]="th.direction" [valueMetric]="th.valueMetric" [aggregation]="th.aggregation"></threshold>
                    <aggregated-value *ngFor="let av of aggregatedValues" [label]="av.label" [aggregation]="av.aggregation" [filter]="av.filter" [unit]="av.unit"></aggregated-value>
               </widget>`,
            viewProviders: [thingContextServiceProvider, contextServiceProvider, exportContextServiceProvider]
        })
        class RegisteredComponent implements AfterContentInit {

            @Input() title: string;

            @Input() config: object;

            @Input() inputs: { [id: string]: string };

            @Input() collapsible: boolean;

            @Input() description: string;

            @ContentChildren(MetricDetailComponent) metrics: QueryList<MetricDetailComponent>;

            @ContentChildren(PropertyComponent) properties: QueryList<PropertyComponent>;

            @ContentChildren(ConfigurationParameterEntryComponent) configParams: QueryList<ConfigurationParameterEntryComponent>;

            @ContentChildren(StatisticComponent) statistics: QueryList<StatisticComponent>;

            @ContentChildren(WorkSessionsComponent) workSessions: QueryList<WorkSessionsComponent>;

            @ContentChildren(ThresholdComponent) thresholds: QueryList<ThresholdComponent>;

            @ContentChildren(AggregatedValueComponent) aggregatedValues: QueryList<AggregatedValueComponent>;

            metricComponents: MetricDetailComponent[] = [];
            propertyComponents: PropertyComponent[] = [];
            configParamsComponents: ConfigurationParameterEntryComponent[] = [];
            statisticComponents: StatisticComponent[] = [];
            workSessionsComponents: WorkSessionsComponent[] = [];
            thresholdComponents: ThresholdComponent[] = [];
            aggregatedValueComponents: AggregatedValueComponent[] = [];
            visible: boolean;

            ngAfterContentInit(): void {
                if (this.metrics?.length) {
                    this.metricComponents = this.metrics.toArray();
                }
                if (this.properties?.length) {
                    this.propertyComponents = this.properties.toArray();
                }
                if (this.configParams?.length) {
                    this.configParamsComponents = this.configParams.toArray();
                }
                if (this.statistics?.length) {
                    this.statisticComponents = this.statistics.toArray();
                }
                if (this.workSessions?.length) {
                    this.workSessionsComponents = this.workSessions.toArray();
                }
                if (this.thresholds?.length) {
                    this.thresholdComponents = this.thresholds.toArray();
                }
                if (this.aggregatedValues?.length) {
                    this.aggregatedValueComponents = this.aggregatedValues.toArray();
                }
                this.visible = true;
            }

        }
        return RegisteredComponent;
    }


    private createModule(component: any, registeredComponents: any[]) {
        @NgModule({
            imports: [
                SharedModule,
                WidgetModule,
                SchedulerModule,
                TimeseriesWidgetModule,
                ThingOptionModule,
                DynamicListModule,
                UserThingAuthorizationModule,
                StyleModule
            ],
            declarations: [component, ...registeredComponents]
        })
        class DynamicModule { }
        return DynamicModule;
    }

    updateControlBarContent(controlBarContent: string): void {
        this.controlBarService.updateContentSubject(controlBarContent)
    }

    loadControlBarContent(content: string): void {
        const controlBarVcRef = this.controlBarService.getVcRef();
        if (controlBarVcRef) {
            if (content) {
                this.loadFromText(content, controlBarVcRef, true);
            } else {
                this.loadFromText("", controlBarVcRef, true);
            }
        }
    }

}
